import React, { lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import RouteFallback from 'components/core/RouteFallback';
import MainLayout from 'layouts/MainLayout';
import GroupsList from 'screens/Groups/GroupsList';
import LoginScreen from 'screens/Login';
import { isAuthenticated } from 'configs/requestConfig';

/*
 * Login and the groups list stay eager — one of them is always the first paint.
 *
 * Everything below is split out. The Studio alone pulls react-moveable,
 * emoji-picker-react, react-datepicker and react-colorful, none of which a signing-in
 * user has any use for.
 */
const StudioScreen = lazy(() => import('screens/Studio/StudioScreen'));
const AudiencesList = lazy(() => import('screens/Audiences/AudiencesList'));
const AudienceWizard = lazy(() => import('screens/Audiences/AudienceWizard'));
const AudienceDetail = lazy(() => import('screens/Audiences/AudienceDetail'));
const SettingsScreen = lazy(() => import('screens/Settings'));

const requireAuth = () => {
  if (!isAuthenticated()) {
    throw redirect({ to: '/login' });
  }
};

const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
});

const mainLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'main',
  component: MainLayout,
  beforeLoad: requireAuth,
});

const listRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/',
  component: GroupsList,
});

const audiencesRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/audiences',
  component: AudiencesList,
});

const audienceNewRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/audiences/new',
  component: AudienceWizard,
});

const audienceDetailRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/audiences/$audienceId',
  component: AudienceDetail,
});

const settingsRoute = createRoute({
  getParentRoute: () => mainLayoutRoute,
  path: '/settings',
  component: SettingsScreen,
});

const studioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/groups/$groupId/studio',
  component: StudioScreen,
  beforeLoad: requireAuth,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  mainLayoutRoute.addChildren([
    listRoute,
    audiencesRoute,
    audienceNewRoute,
    audienceDetailRoute,
    settingsRoute,
  ]),
  studioRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
